import { createContext, PropsWithChildren, useContext, useRef } from "react";

type InformationRequestSaves = {
  trackSave: <Result>(save: Promise<Result>) => Promise<Result>;
  /** Resolves to whether every save that was still running succeeded. */
  waitForSaves: () => Promise<boolean>;
};

const InformationRequestSavesContext = createContext<InformationRequestSaves>({
  trackSave: (save) => save,
  waitForSaves: async () => true,
});

export function InformationRequestSavesProvider({
  children,
}: PropsWithChildren) {
  const pendingSaves = useRef(new Set<Promise<unknown>>());
  const saves = useRef<InformationRequestSaves>({
    trackSave: (save) => {
      pendingSaves.current.add(save);
      save
        .finally(() => pendingSaves.current.delete(save))
        .catch(() => undefined);
      return save;
    },
    waitForSaves: async () => {
      const results = await Promise.allSettled([...pendingSaves.current]);
      return results.every((result) => result.status === "fulfilled");
    },
  });

  return (
    <InformationRequestSavesContext.Provider value={saves.current}>
      {children}
    </InformationRequestSavesContext.Provider>
  );
}

export function useInformationRequestSaves() {
  return useContext(InformationRequestSavesContext);
}
